import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCurrentLang from '@/hooks/useCurrentLang';
import useResize from '@/hooks/useResize';
import { Box, Flex, LoadingPage } from '@/layout';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { sify } from 'chinese-conv/dist';
import PATH from '@/router/path';
import { postBridgeSecret, generateBridgeSecret } from '@/api/bridge.api';
import { Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';
import { PageDescription, PageTitle, PageWrapper } from './style';
import { useAppDispatch, useAppSelector } from '@/redux/store.hook';
import { getAuthSelector, signOut } from '@/redux/auth/auth.slice';
import { disconnect } from '@/redux/notification/notification.slice';
import { clearCart } from '@/redux/cart/cart.slice';

const BridgeSecret = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const { isTablet, isMobile } = useResize();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useAppDispatch();
  const { isLogin } = useAppSelector(getAuthSelector);

  const initData = async () => {
    setIsLoading(true);

    try {
      // 檢查 URL 是否帶有 secret 參數
      const secret = searchParams.get('secret');

      if (!secret) {
        setErrorMessage(t('pages.bridge.noToken'));
        return;
      }

      // 若有參數則送出 API
      const response = await postBridgeSecret({ secret: encodeURIComponent(secret) });

      // 根據回應內容判斷
      if (response.data.reset_token) {
        // 有 reset_token 表示成功，導向設定密碼頁面
        navigate(PATH.bridge.setPassword.replace(':lang', lang), {
          state: {
            token: response.data.reset_token,
            email: response.data.user.email,
          },
        });
      } else if (response.data.message === 'Expired secret.') {
        // token 過期
        setErrorMessage(t('pages.bridge.expiredSecret'));
      } else if (response.data.message === 'user exists') {
        setErrorMessage('user exists');
      } else {
        // 其他錯誤訊息
        setErrorMessage(t('pages.bridge.generalError'));
      }
    } catch (error: any) {
      // 處理網絡錯誤或其他未預期的錯誤
      errorHandler(t('error.server'));
      setTimeout(() => {
        navigate(PATH.home.index);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLogin) {
      dispatch(disconnect());
      dispatch(signOut());
      dispatch(clearCart());
    }
  }, [dispatch, isLogin]);

  useEffect(() => {
    initData();
  }, []);

  return isLoading ? (
    <LoadingPage isLoading={isLoading} />
  ) : (
    <PageWrapper isMobile={isMobile}>
      <PageTitle>{
        errorMessage && errorMessage !== 'user exists' ? (
          t('pages.bridge.title')
        ) : (
          t('pages.bridge.userExists.title')
        )
      }</PageTitle>
      <Flex flexDirection={'column'} alignItems={'center'} gridGap={'20px'}>
        {
          errorMessage && errorMessage !== 'user exists' ? (
            <PageDescription>{errorMessage}</PageDescription>
          ) : (
            <PageDescription>
              {t('pages.bridge.userExists.description_front')}<br />
              {t('pages.bridge.userExists.description_back_1')}
              <Link to={PATH.auth.signIn} style={{ color: theme.colors.primary500, fontWeight: 'bold' }}>
                {t('pages.bridge.userExists.signIn')}
              </Link>
              {t('pages.bridge.userExists.description_back_2')}
            </PageDescription>
          )
        }
      </Flex>
    </PageWrapper>
  );
};

export default BridgeSecret;
